import graphql__f0a668acecc8a2b50b73 from "./__generated__/MapElementsVectorGridTileLayerStyles_map.graphql.js";import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import memoize from "lodash/memoize";

import getMapElementStyle from "../../Utils/getMapElementStyle";
import styleCreationFunctions from "./MapElementsVectorGridTileLayerStyleCreator";
import { useMapElementsFilteredIn } from "./useMapElementsFilteredIn";

// Elements with zoom level specific styles get a style function, that the vector grid
// calls with the zoom level of each tile it renders. As tiles are rendered again for
// every zoom level, this is enough to display the style matching the current zoom,
// without listening to the map zoom changes.
//
// The other elements keep a static style object, to avoid resolving the same style for
// every feature of every tile.
//
// The options don't depend on the element style, like the filtered out state, so they're
// merged into the resolved style for the vector grid renderer to read them.
const tileLayerStyleFor = (formatElementStyle, style, options) => {
  if (!style?.zoomLevels?.length) return { ...formatElementStyle(style), ...options };

  // The style is resolved once per zoom level, as the vector grid calls the style
  // function for each feature of each tile, and point styles create a new Leaflet icon
  // each time they're resolved.
  const styleForZoomLevel = memoize(zoomLevel => ({
    ...formatElementStyle(getMapElementStyle(zoomLevel, style)),
    ...options
  }));

  return (_properties, zoomLevel) => styleForZoomLevel(zoomLevel);
};

export const useFormatVectorTileLayerStyles = props => {
  const map = useFragment(MapElementsVectorGridTileLayerStyles_map, props.map);

  const elements = useMemo(
    () => map.elements.edges.map(({ node }) => node),
    [map.elements.edges]
  );

  const elementsWithFilteredInProp = useMapElementsFilteredIn({ map, elements });

  return useMemo(() => {
    return elementsWithFilteredInProp.reduce((styles, mapElement) => {
      const { id: layerName, geometriesType, style, isFilteredIn } = mapElement;

      // If the element has no geometriesType, it means that it doesn't have an associated
      // geometry, so we don't try to create any style for it.
      if (!geometriesType) return styles;

      // Define the function to call based on the geometriesType
      const formatElementStyle = styleCreationFunctions[geometriesType];
      // The geometries of filtered out elements stay loaded in the vector tiles, so they
      // are flagged as hidden to let the renderer neither display nor make them
      // interactive.
      styles[layerName] = tileLayerStyleFor(formatElementStyle, style, {
        isHidden: !isFilteredIn
      });

      return styles;
    }, {});
  }, [elementsWithFilteredInProp]);
};

const MapElementsVectorGridTileLayerStyles_map = graphql__f0a668acecc8a2b50b73;
